import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuGroup } from "@/components/ui/dropdown-menu";
import { Calculator, Search, PieChart, BookOpen, Phone, LayoutDashboard, LogOut, User, Menu, X, TrendingUp, CalendarDays, Award, Filter, BarChart3, ChevronDown, LineChart, Landmark, Building2, Wallet, ArrowRightLeft, Scale, Percent, Grid3X3 } from "lucide-react";

export const Navbar = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navLinks = [
    { to: "/calculators", label: "Calculators", icon: Calculator },
    { 
      label: "MF Research", 
      icon: Search,
      isDropdown: true,
      children: [
        { type: "label", label: "Popular Tools" },
        { to: "/research/sip-calculator", label: "SIP Calculator", icon: Calculator },
        { to: "/research/lumpsum-calculator", label: "Lumpsum Calculator", icon: Wallet },
        { to: "/research/swp-calculator", label: "SWP Calculator", icon: Wallet },
        { to: "/research/stp-calculator", label: "STP Calculator", icon: ArrowRightLeft },
        { type: "separator" },
        { type: "label", label: "Fund Analysis" },
        { to: "/top-performers", label: "Top Performers", icon: Award },
        { to: "/research/top-funds-amc", label: "Top Funds - AMC Wise", icon: Building2 },
        { to: "/research/trailing-returns", label: "Trailing Returns", icon: BarChart3 },
        { to: "/screener", label: "Fund Screener", icon: Filter },
        { to: "/compare", label: "Compare Funds", icon: TrendingUp },
        { type: "separator" },
        { type: "label", label: "Comparisons" },
        { to: "/research/ppf-vs-elss", label: "PPF vs ELSS", icon: Scale },
        { to: "/fd-vs-debt", label: "FD vs Debt Funds", icon: Landmark },
        { to: "/research/liquid-vs-savings", label: "Liquid vs Savings", icon: Percent },
        { type: "separator" },
        { to: "/research-tools", label: "View All 40+ Tools", icon: Grid3X3, highlight: true },
      ]
    },
    { to: "/blog", label: "Learn", icon: BookOpen },
    { to: "/appointments", label: "Book Meeting", icon: CalendarDays },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="nav-pill" data-testid="navbar">
      <div className="flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
          <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center">
            <span className="text-primary-foreground font-bold text-lg font-serif">P</span>
          </div>
          <span className="font-serif text-xl font-semibold text-foreground hidden sm:block">Paisa Secure</span>
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-1">
          {navLinks.map((link) => {
            const Icon = link.icon;
            
            if (link.isDropdown) {
              return (
                <DropdownMenu key={link.label}>
                  <DropdownMenuTrigger asChild>
                    <button
                      className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                        link.children.some(child => child.to && isActive(child.to))
                          ? "bg-primary text-primary-foreground"
                          : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                      }`}
                      data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
                    >
                      <Icon className="w-4 h-4" />
                      {link.label}
                      <ChevronDown className="w-3 h-3" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="center" className="w-64 max-h-[70vh] overflow-y-auto">
                    {link.children.map((child, idx) => {
                      if (child.type === "label") {
                        return <DropdownMenuLabel key={idx} className="text-xs text-muted-foreground">{child.label}</DropdownMenuLabel>;
                      }
                      if (child.type === "separator") {
                        return <DropdownMenuSeparator key={idx} />;
                      }
                      const ChildIcon = child.icon;
                      return (
                        <DropdownMenuItem key={child.to} asChild>
                          <Link
                            to={child.to}
                            className={`flex items-center gap-2 cursor-pointer ${child.highlight ? 'text-primary font-medium' : ''}`}
                            data-testid={`nav-${child.label.toLowerCase().replace(/\s+/g, '-')}`}
                          >
                            <ChildIcon className="w-4 h-4" />
                            {child.label}
                          </Link>
                        </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>
              );
            }
            
            return (
              <Link
                key={link.to}
                to={link.to}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                  isActive(link.to)
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                }`}
                data-testid={`nav-${link.label.toLowerCase()}`}
              >
                <Icon className="w-4 h-4" />
                {link.label}
              </Link>
            );
          })}
        </div>

        {/* Auth Buttons */}
        <div className="flex items-center gap-3">
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-trigger">
                  {user.picture ? (
                    <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                      <User className="w-4 h-4 text-primary-foreground" />
                    </div>
                  )}
                  <span className="hidden sm:block text-sm">{user.name?.split(' ')[0]}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 bg-card border-border">
                <DropdownMenuItem asChild>
                  <Link to="/dashboard" className="flex items-center gap-2 cursor-pointer" data-testid="menu-dashboard">
                    <LayoutDashboard className="w-4 h-4" />
                    Dashboard
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/portfolio" className="flex items-center gap-2 cursor-pointer" data-testid="menu-portfolio">
                    <PieChart className="w-4 h-4" />
                    Portfolio
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} className="flex items-center gap-2 cursor-pointer text-destructive" data-testid="menu-logout">
                  <LogOut className="w-4 h-4" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Link to="/login">
                <Button variant="ghost" className="text-sm hidden sm:inline-flex" data-testid="login-btn">
                  Sign In
                </Button>
              </Link>
              <Link to="/register">
                <Button className="btn-primary text-sm rounded-full px-6" data-testid="get-started-btn">
                  Get Started
                </Button>
              </Link>
            </>
          )}

          {/* Mobile Menu Button */}
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden mt-4 pt-4 border-t border-border">
          <div className="flex flex-col gap-2">
            {navLinks.map((link) => {
              const Icon = link.icon;
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                    isActive(link.to)
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  {link.label}
                </Link>
              );
            })}
            {user && (
              <>
                <Link
                  to="/dashboard"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary"
                >
                  <LayoutDashboard className="w-5 h-5" />
                  Dashboard
                </Link>
                <Link
                  to="/portfolio"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary"
                >
                  <PieChart className="w-5 h-5" />
                  Portfolio
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};
